import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { isWSStatusErrorResponse } from '@qtek/core/websockets-core';
import { Customer, EntityMeta } from '@qtek/shared/models';
import {
  catchError,
  filter,
  finalize,
  mergeMap,
  of,
  pipe,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { TASK_FORM_MYSID } from '../tasks/task-form.selectors';
import { CustomersService } from './customers.service';

export interface NewRelationsState {
  people: Record<string, Customer[]>;
  peopleMeta: Record<string, EntityMeta>;
  peopleLoading: Record<string, boolean>;
  institutions: Customer[];
  institutionLoading: boolean;
  institutionsMeta: EntityMeta | null;
  availableInstitutions: Customer[];
  availableInstitutionsLoading: boolean;
  availableInstitutionsMeta: EntityMeta | null;
  selectedCustomer: Customer | null;
  selectedCompany: Customer | null;
  error: Error | null;
  removeContactResult: { payload: unknown } | { error: Error } | null;
  updatePeopleResult: { customer: Customer } | { error: Error } | null;
  createPeopleResult: { customer: Customer } | { error: Error } | null;
  updateInstitutionsResult: { customer: Customer } | { error: Error } | null;
  /** Mysids with a live connectPeople subscription — guards against connecting twice. */
  connectedPeopleMysids: Record<string, boolean>;
}

const initialState: NewRelationsState = {
  people: {},
  peopleMeta: {},
  peopleLoading: {},
  institutions: [],
  institutionLoading: false,
  institutionsMeta: null,
  availableInstitutions: [],
  availableInstitutionsLoading: false,
  availableInstitutionsMeta: null,
  selectedCustomer: null,
  selectedCompany: null,
  error: null,
  removeContactResult: null,
  updatePeopleResult: null,
  createPeopleResult: null,
  updateInstitutionsResult: null,
  connectedPeopleMysids: {},
};

const httpErrorStatus = (error: unknown) =>
  (error as { error?: { sts?: unknown } })?.error?.sts ?? error;

const disconnect$ = new Subject<string>();
/** Mysids that should never disconnect — kept live for the whole app session. */
const PERSISTENT_PEOPLE_MYSIDS = new Set<string>([
  TASK_FORM_MYSID.ALL_CONTACTS,
]);

export const NewRelationsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store, customersService = inject(CustomersService)) => {
    return {
      // ── WS: People (multi-channel concurrent) ────────────────────────────────
      connectPeople: rxMethod<{
        op?: 'sub' | 'fast_query';
        mysid: string;
        params?: any;
        view?: string;
        q?: any;
      }>(
        pipe(
          // Reuse an already-connected mysid instead of opening a second subscription.
          filter(({ mysid }) => {
            if (store.connectedPeopleMysids()[mysid]) return false;
            patchState(store, state => ({
              connectedPeopleMysids: {
                ...state.connectedPeopleMysids,
                [mysid]: true,
              },
              peopleLoading: { ...state.peopleLoading, [mysid]: true },
            }));
            return true;
          }),
          mergeMap(action =>
            customersService
              .subscribe({
                mysid: action.mysid,
                view: action.view ?? 'contacts',
                prms: action.params,
                ...(action.q ? { q: action.q } : {}),
                ...(action.op ? { op: action.op } : {}),
              })
              .pipe(
                takeUntil(disconnect$.pipe(filter(id => id === action.mysid))),
                finalize(() =>
                  patchState(store, state => {
                    const {
                      [action.mysid]: _removed,
                      ...connectedPeopleMysids
                    } = state.connectedPeopleMysids;
                    return { connectedPeopleMysids };
                  })
                ),
                tap((data: any) => {
                  const { op, sts, mysid } = data;
                  const res = Array.isArray(data.res) ? data.res : data.res;
                  switch (op) {
                    case 'query':
                    case 'fast_query':
                      if (isWSStatusErrorResponse(sts)) {
                        patchState(store, state => ({
                          peopleLoading: {
                            ...state.peopleLoading,
                            [mysid]: false,
                          },
                        }));
                      } else {
                        patchState(store, state => ({
                          people: { ...state.people, [mysid]: res ?? [] },
                          peopleLoading: {
                            ...state.peopleLoading,
                            [mysid]: false,
                          },
                          peopleMeta: {
                            ...state.peopleMeta,
                            [mysid]: sts?.meta || {},
                          },
                        }));
                      }
                      break;
                    case 'ins':
                      if (!isWSStatusErrorResponse(sts) && res) {
                        patchState(store, {
                          createPeopleResult: { customer: res },
                        });
                        customersService.query({
                          mysid: action.mysid,
                          view: action.view ?? 'contacts',
                          ...(action.params ? { prms: action.params } : {}),
                        });
                      } else if (isWSStatusErrorResponse(sts)) {
                        patchState(store, {
                          createPeopleResult: { error: sts },
                        });
                      }
                      break;
                    case 'upd': {
                      if (!isWSStatusErrorResponse(sts) && res) {
                        patchState(store, state => {
                          const existingPeople = state.people[mysid] || [];
                          const id = res.relId;
                          const hasMatch =
                            id != null &&
                            existingPeople.some(c => c._id === id);
                          let nextPeople: Customer[];
                          if (hasMatch) {
                            nextPeople = existingPeople.map(c =>
                              id === c._id ? res : c
                            );
                          } else if (id != null) {
                            nextPeople = [...existingPeople, res];
                          } else {
                            nextPeople = existingPeople;
                          }
                          return {
                            people: { ...state.people, [mysid]: nextPeople },
                            updatePeopleResult: { customer: res },
                          };
                        });
                      } else if (isWSStatusErrorResponse(sts)) {
                        patchState(store, {
                          updatePeopleResult: { error: sts },
                        });
                      }
                      break;
                    }
                    case 'del':
                      if (!isWSStatusErrorResponse(sts) && res) {
                        const deletedIds = [data.id, res._id, res.user].filter(
                          Boolean
                        );
                        patchState(store, state => ({
                          people: {
                            ...state.people,
                            [mysid]: (state.people[mysid] ?? []).filter(
                              c => !deletedIds.includes(c._id)
                            ),
                          },
                        }));
                      }
                      break;
                  }
                }),
                catchError(error => {
                  patchState(store, state => ({
                    peopleLoading: {
                      ...state.peopleLoading,
                      [action.mysid]: false,
                    },
                    error,
                  }));
                  return of(null);
                })
              )
          )
        )
      ),

      disconnectPeople(mysid: string): void {
        if (PERSISTENT_PEOPLE_MYSIDS.has(mysid)) {
          return;
        }
        disconnect$.next(mysid);
        patchState(store, state => {
          const { [mysid]: _p, ...people } = state.people;
          const { [mysid]: _m, ...peopleMeta } = state.peopleMeta;
          const { [mysid]: _l, ...peopleLoading } = state.peopleLoading;
          const { [mysid]: _c, ...connectedPeopleMysids } =
            state.connectedPeopleMysids;
          return { people, peopleMeta, peopleLoading, connectedPeopleMysids };
        });
      },

      // ── WS: Institutions (multi-channel concurrent) ───────────────────────────
      connectInstitutions: rxMethod<{ mysid: string }>(
        pipe(
          tap(() => patchState(store, { institutionLoading: true })),
          mergeMap(({ mysid }) =>
            customersService.subscribe({ mysid, view: 'companies' }).pipe(
              takeUntil(disconnect$.pipe(filter(id => id === mysid))),
              tap((data: any) => {
                const { op, sts } = data;
                const res = Array.isArray(data.res) ? data.res : data.res;
                switch (op) {
                  case 'query':
                    if (isWSStatusErrorResponse(sts)) {
                      patchState(store, { institutionLoading: false });
                    } else if (res) {
                      patchState(store, {
                        institutions: res,
                        institutionLoading: false,
                        institutionsMeta: sts?.meta || {},
                      });
                    } else {
                      patchState(store, { institutionLoading: false });
                    }
                    break;
                  case 'ins':
                    if (res) {
                      patchState(store, state => ({
                        institutions: [...state.institutions, res],
                      }));
                    }
                    break;
                  case 'upd':
                    if (res) {
                      patchState(store, state => ({
                        institutions: state.institutions.map(c =>
                          res.company?._id === c.company?._id ? res : c
                        ),
                      }));
                    }
                    break;
                  case 'del':
                    if (res) {
                      patchState(store, state => ({
                        institutions: state.institutions.filter(
                          c => c._id !== res._id
                        ),
                      }));
                    }
                    break;
                }
              }),
              catchError(error => {
                patchState(store, { institutionLoading: false, error });
                return of(null);
              })
            )
          )
        )
      ),

      disconnectInstitutions(mysid: string): void {
        disconnect$.next(mysid);
      },

      // ── WS: Customer by id (multi-channel concurrent) ─────────────────────────
      connectCustomerById: rxMethod<{ mysid: string; usrId: string }>(
        pipe(
          tap(() => patchState(store, { selectedCustomer: null })),
          mergeMap(({ mysid, usrId }) =>
            customersService
              .subscribeSingleContactById({
                mysid,
                view: 'contacts',
                cmd: 'query',
              })
              .pipe(
                takeUntil(disconnect$.pipe(filter(id => id === mysid))),
                tap((data: any) => {
                  if (data.op === 'query') {
                    const match = (data.res ?? []).find(
                      (c: Customer) => c._id === usrId
                    );
                    if (match) {
                      patchState(store, { selectedCustomer: match });
                    } else {
                      patchState(store, {
                        selectedCustomer: null,
                        error: data.sts,
                      });
                    }
                  }
                }),
                catchError(error => {
                  patchState(store, { selectedCustomer: null, error });
                  return of(null);
                })
              )
          )
        )
      ),

      disconnectCustomerById(mysid: string): void {
        disconnect$.next(mysid);
      },

      // ── WS: Single institution (multi-channel concurrent) ─────────────────────
      connectSingleInstitution: rxMethod<{
        mysid: string;
        relAcnId: string;
      }>(
        pipe(
          tap(() => patchState(store, { selectedCompany: null })),
          mergeMap(({ mysid, relAcnId }) =>
            customersService
              .subscribe({
                mysid,
                view: 'companies',
                cmd: 'query',
              })
              .pipe(
                takeUntil(disconnect$.pipe(filter(id => id === mysid))),
                tap((data: any) => {
                  if (data.op === 'query') {
                    const match = (data.res ?? []).find(
                      (c: Customer) => c._id === relAcnId
                    );
                    if (match) {
                      patchState(store, {
                        selectedCompany: {
                          ...match,
                          acn: {
                            _id: match.company?._id,
                            companyName: match.company?.companyName,
                            lgoImg: match.company?.logoImage,
                          },
                        } as Customer,
                      });
                    } else {
                      patchState(store, {
                        selectedCompany: null,
                        error: data.sts,
                      });
                    }
                  }
                }),
                catchError(error => {
                  patchState(store, { selectedCompany: null, error });
                  return of(null);
                })
              )
          )
        )
      ),

      disconnectSingleInstitution(mysid: string): void {
        disconnect$.next(mysid);
      },

      // ── WS: Available institutions (multi-channel concurrent) ─────────────────
      connectAvailableInstitutions: rxMethod<{
        mysid: string;
        itmId: string;
      }>(
        pipe(
          tap(() => patchState(store, { availableInstitutionsLoading: true })),
          mergeMap(({ mysid, itmId }) =>
            customersService
              .subscribe({
                mysid,
                view: 'companies',
                cmd: 'query',
                // A `view` q key would overwrite the view selector above.
                q: [{ key: 'itmId', op: 'EQ', vals: [itmId] }],
              })
              .pipe(
                takeUntil(disconnect$.pipe(filter(id => id === mysid))),
                tap((data: any) => {
                  if (data.op === 'query') {
                    if (data.res) {
                      patchState(store, {
                        availableInstitutions: data.res,
                        availableInstitutionsMeta: data.sts?.meta || {},
                        availableInstitutionsLoading: false,
                      });
                    } else {
                      patchState(store, {
                        availableInstitutionsLoading: false,
                        error: data.sts,
                      });
                    }
                  }
                }),
                catchError(error => {
                  patchState(store, {
                    availableInstitutionsLoading: false,
                    error,
                  });
                  return of(null);
                })
              )
          )
        )
      ),

      disconnectAvailableInstitutions(mysid: string): void {
        disconnect$.next(mysid);
      },

      // ── HTTP methods ─────────────────────────────────────────────────────────
      createPeople: rxMethod<Customer>(
        pipe(
          tap(() => patchState(store, { createPeopleResult: null })),
          switchMap(customer =>
            customersService.createCustomer(customer).pipe(
              tap(({ res }) =>
                patchState(store, { createPeopleResult: { customer: res } })
              ),
              catchError(error => {
                patchState(store, {
                  createPeopleResult: {
                    error: httpErrorStatus(error) as Error,
                  },
                });
                return of(null);
              })
            )
          )
        )
      ),

      deletePeople: rxMethod<string>(
        pipe(
          switchMap(relId =>
            customersService
              .deleteCustomer(relId)
              .pipe(catchError(() => of(null)))
          )
        )
      ),

      updatePeople: rxMethod<Partial<Customer>>(
        pipe(
          tap(() => patchState(store, { updatePeopleResult: null })),
          switchMap(customer =>
            customersService
              .updateCustomer(customer as Customer, customer._id)
              .pipe(
                tap(({ res }) =>
                  patchState(store, {
                    updatePeopleResult: { customer: res },
                  })
                ),
                catchError(error => {
                  patchState(store, {
                    updatePeopleResult: {
                      error: httpErrorStatus(error) as Error,
                    },
                  });
                  return of(null);
                })
              )
          )
        )
      ),

      removeContact: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { removeContactResult: null })),
          switchMap(relId =>
            customersService.removeCustomer(relId).pipe(
              tap(({ res }) =>
                patchState(store, {
                  removeContactResult: { payload: res },
                })
              ),
              catchError((error: Error) => {
                patchState(store, { removeContactResult: { error } });
                return of(null);
              })
            )
          )
        )
      ),

      updateInstitutions: rxMethod<{
        customer: Partial<Customer>;
        mysid: string;
      }>(
        pipe(
          tap(() => patchState(store, { updateInstitutionsResult: null })),
          switchMap(({ customer }) =>
            customersService.updateInstitution(customer).pipe(
              tap(({ res }) =>
                patchState(store, {
                  updateInstitutionsResult: { customer: res },
                })
              ),
              catchError(error => {
                patchState(store, {
                  updateInstitutionsResult: {
                    error: httpErrorStatus(error) as Error,
                  },
                });
                return of(null);
              })
            )
          )
        )
      ),

      // ── Fire-and-forget plain methods ─────────────────────────────────────────
      getCustomerById({ id, mysid }: { id: string; mysid: string }): void {
        customersService.getSingleContactById({ id, mysid, view: 'prs' });
      },

      clearAvailableInstitutions(): void {
        patchState(store, {
          availableInstitutions: [],
          availableInstitutionsMeta: null,
          availableInstitutionsLoading: false,
        });
      },

      resetPeopleResults(): void {
        patchState(store, {
          createPeopleResult: null,
          updatePeopleResult: null,
        });
      },

      // ── Parameterized derived state ───────────────────────────────────────────
      contactsByMysid(mysid: string) {
        return computed(() => store.people()[mysid] ?? []);
      },

      contactsMetaByMysid(mysid: string) {
        return computed(() => store.peopleMeta()[mysid] ?? null);
      },

      contactsLoadingByMysid(mysid: string) {
        return computed(() => store.peopleLoading()[mysid] ?? false);
      },
    };
  }),

  withComputed(({ selectedCustomer, selectedCompany }) => ({
    currentSelectedCustomer: computed(() => selectedCustomer()),
    currentSelectedCompany: computed(() => selectedCompany()),
  })),

  withHooks({
    onDestroy() {
      disconnect$.complete();
    },
  })
);
